import { useForm } from "@formiz/core";
import { isMaxLength, isMinLength, isRequired } from "@formiz/validations";
import { NotificationType, useTranslatedVoiceLanguage, useTranslation, useVoice } from "@next-platform/client-common-core";
import type { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { BlockerFunction } from "react-router-dom";

import useToast from "@/hooks/useToast";
import useFileDragAndDrop from "@/pages/Chat/hooks/useFileDragAndDrop";
import { createVoiceClipValidationRules, MAX_VOICE_CLIPS } from "@/pages/Voice/CloneVoice/utils/createVoiceClipValidationRules";
import { arrLen, hasErroredPlaceholderClips, isSameFile, trimStr } from "@/pages/Voice/CloneVoice/utils/voiceFormHelpers";
import {
  cloneVoiceRequiredFields,
  DESCRIPTION_MAX_LENGTH,
  DESCRIPTION_MIN_LENGTH,
  VOICE_NAME_MAX_LENGTH,
  VOICE_NAME_MIN_LENGTH,
} from "@/pages/Voice/constants";
import { VoiceClipPlaceholder, VoiceFormFields, VoiceFormPayload, VoiceFormProps } from "@/pages/Voice/types";
import { findFirstInvalidFormizField, isStringContainingURL, scrollToFirstInvalidField } from "@/utils";

import { IValidationRef } from "./CloneVoiceForm";
import { useCloneVoiceForm } from "./CloneVoiceFormContext";

const acceptedFileTypes = ["audio/mpeg", "audio/mp3", "audio/wav", "audio/wave", "audio/x-wav"];

export const EMPTY_ARRAY: never[] = [];

export function buildNameValidationRules(t: TFunction) {
  return [
    {
      rule: (value: string) => isRequired()(value?.trim()),
      message: t("voice:form.validation.name.required"),
    },
    {
      rule: (value: string) => isMinLength(VOICE_NAME_MIN_LENGTH)(value?.trim()),
      message: t("voice:form.validation.name.tooShort", { min: VOICE_NAME_MIN_LENGTH }),
    },
    {
      rule: (value: string) => !isStringContainingURL(value),
      message: t("voice:form.validation.name.noUrls"),
    },
    {
      rule: (value: string) => isMaxLength(VOICE_NAME_MAX_LENGTH)(value?.trim()),
      message: t("voice:form.validation.name.tooLong", { max: VOICE_NAME_MAX_LENGTH }),
    },
  ];
}

export function buildLanguageValidationRules(t: TFunction) {
  return [
    {
      rule: (value: string) => isRequired()(value?.trim()),
      message: t("voice:form.validation.language.required"),
    },
  ];
}

export function buildDescriptionValidationRules(t: TFunction) {
  return [
    {
      rule: (value: string) => isRequired()(value?.trim()),
      message: t("voice:form.validation.description.required"),
    },
    {
      rule: (value: string) => isMinLength(DESCRIPTION_MIN_LENGTH)(value?.trim()),
      message: t("voice:form.validation.description.tooShort", { min: DESCRIPTION_MIN_LENGTH }),
    },
    {
      rule: (value: string) => isMaxLength(DESCRIPTION_MAX_LENGTH)(value?.trim()),
      message: t("voice:form.validation.description.tooLong", { max: DESCRIPTION_MAX_LENGTH }),
    },
    {
      rule: (value: string) => !isStringContainingURL(value),
      message: t("voice:form.validation.description.noUrls"),
    },
  ];
}

export function getValidationSummary(fields: Record<string, { error?: string }>) {
  const errors: string[] = [];
  Object.keys(fields).forEach((fieldName: string) => {
    const field = fields[fieldName];
    if (field.error) errors.push(field.error);
  });
  return errors;
}

type VoiceFormInstance = ReturnType<typeof useForm>;

function useFormInitialization(props: VoiceFormProps, voiceForm: VoiceFormInstance, refValidClips: React.RefObject<IValidationRef>) {
  const { id = "", name = "", description = "", language = "", voiceClip = EMPTY_ARRAY, voiceClipPlaceholders = EMPTY_ARRAY, setShowAlert } = props;

  const {
    formData: contextFormData,
    updateFormData: updateContextFormData,
    hasData: hasContextData,
    referenceValues: contextReferenceValues,
    setReferenceValues: setContextReferenceValues,
  } = useCloneVoiceForm();

  const { t } = useTranslation();
  const { getTranslatedVoiceLanguage } = useTranslatedVoiceLanguage();
  const { getVoiceLanguages } = useVoice();
  const { displayToast } = useToast();
  const [stateBackendLanguages, setStateBackendLanguages] = useState<string[]>([]);
  const [stateIsLanguagesLoading, setStateIsLanguagesLoading] = useState(true);

  useEffect(() => {
    getVoiceLanguages()
      .then((languages) => {
        if (languages?.length) setStateBackendLanguages(languages);
        else throw new Error();
      })
      .catch(() => displayToast({ message: t("voice:form.create.error.noLanguagesAvailable"), type: NotificationType.error }))
      .finally(() => setStateIsLanguagesLoading(false));
  }, [displayToast, getVoiceLanguages]);

  const stateCloneLanguages = useMemo(
    () => stateBackendLanguages.map((code) => getTranslatedVoiceLanguage(code) || code),
    [stateBackendLanguages, getTranslatedVoiceLanguage],
  );

  const initialValues = useMemo(
    () => ({
      [VoiceFormFields.VOICE_NAME]: contextFormData.name || name,
      [VoiceFormFields.DESCRIPTION]: contextFormData.desription || description,
      [VoiceFormFields.LANGUAGE]: contextFormData.language || language,
      [VoiceFormFields.VOICE_CLIP]: contextFormData.voiceClip || voiceClip,
      ...(voiceClipPlaceholders ? { [VoiceFormFields.PLACEHOLDER_CLIP]: voiceClipPlaceholders } : {}),
    }),
    [
      contextFormData.name,
      contextFormData.desription,
      contextFormData.language,
      contextFormData.voiceClip,
      name,
      description,
      language,
      voiceClip,
      voiceClipPlaceholders,
    ],
  );

  const { isValid, setFieldsValues, isStepPristine, values } = voiceForm;
  const refInitialValuesSnapshot = useRef<Record<string, unknown> | null>(null);
  const refHasInitialized = useRef(false);
  const isErrorMode = (voiceClipPlaceholders?.length ?? 0) > 0;
  const placeholderVoiceClips = voiceForm.values?.placeholderVoiceClip ?? voiceClipPlaceholders;
  const hasErroredPlaceholderClip = hasErroredPlaceholderClips(placeholderVoiceClips);
  const voiceClipValidationRules = createVoiceClipValidationRules(
    refValidClips.current!.clips,
    arrLen(placeholderVoiceClips),
    hasErroredPlaceholderClip,
    t,
  );

  useEffect(() => {
    if (!refInitialValuesSnapshot.current && values && Object.keys(values).length > 0) {
      if (!hasContextData || Object.keys(contextReferenceValues).length === 0) {
        refInitialValuesSnapshot.current = { ...values };
        setContextReferenceValues(values);
      } else {
        refInitialValuesSnapshot.current = { ...contextReferenceValues };
      }
    }
  }, [values, hasContextData, contextReferenceValues, setContextReferenceValues]);

  useEffect(() => {
    if (!refHasInitialized.current) {
      setFieldsValues(initialValues);
      refHasInitialized.current = true;
    }
  }, [initialValues, setFieldsValues]);

  useEffect(() => {
    if (!id && hasContextData) {
      updateContextFormData({});
      setContextReferenceValues({});
    }
  }, [id, hasContextData, updateContextFormData, setContextReferenceValues]);

  const hasFormChanged = useMemo(() => {
    const snapshot = refInitialValuesSnapshot.current;
    if (!snapshot) return false;
    return (
      trimStr(values[VoiceFormFields.VOICE_NAME]) !== trimStr(snapshot[VoiceFormFields.VOICE_NAME]) ||
      trimStr(values[VoiceFormFields.DESCRIPTION]) !== trimStr(snapshot[VoiceFormFields.DESCRIPTION]) ||
      values[VoiceFormFields.LANGUAGE] !== snapshot[VoiceFormFields.LANGUAGE] ||
      arrLen(values[VoiceFormFields.VOICE_CLIP]) !== arrLen(snapshot[VoiceFormFields.VOICE_CLIP]) ||
      arrLen(values[VoiceFormFields.PLACEHOLDER_CLIP]) !== arrLen(snapshot[VoiceFormFields.PLACEHOLDER_CLIP])
    );
  }, [values]);

  useEffect(() => {
    setShowAlert?.(!isValid || hasErroredPlaceholderClip || (isErrorMode && !hasFormChanged));
  }, [isValid, hasErroredPlaceholderClip, isErrorMode, hasFormChanged, setShowAlert]);

  return {
    t,
    stateBackendLanguages,
    stateIsLanguagesLoading,
    stateCloneLanguages,
    initialValues,
    isErrorMode,
    voiceClipValidationRules,
    isValid,
    isStepPristine,
    values,
    hasFormChanged,
    hasErroredPlaceholderClip,
  };
}

function useFileHandling(voiceForm: VoiceFormInstance) {
  const { values, setFieldsValues } = voiceForm;

  const filterAudioFiles = useCallback(
    (files: File[] | FileList | null) => {
      if (!files?.length) return EMPTY_ARRAY;
      const isDuplicate = (file: File) => voiceForm.values.voiceClip.some((clip: File) => isSameFile(clip, file));
      return Array.from(files).filter((file) => acceptedFileTypes.includes(file.type) && !isDuplicate(file));
    },
    [voiceForm.values.voiceClip],
  );

  const handleFilesAppend = useCallback(
    (files: File[]) => {
      setFieldsValues({ [VoiceFormFields.VOICE_CLIP]: [...values.voiceClip, ...files] }, { keepPristine: true });
    },
    [setFieldsValues, values.voiceClip],
  );

  const handleFilePicker = useCallback(
    (event?: Event) => {
      event?.preventDefault();
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".mp3,.wav,audio/mpeg,audio/mp3,audio/wav,audio/wave,audio/x-wav";
      input.multiple = true;
      input.onchange = (e: Event) => {
        handleFilesAppend(filterAudioFiles((e.target as HTMLInputElement).files));
      };
      input.showPicker();
    },
    [filterAudioFiles, handleFilesAppend],
  );

  const handleFilesDrop = useCallback(
    (files: File[]) => handleFilesAppend(filterAudioFiles(Array.from(files))),
    [filterAudioFiles, handleFilesAppend],
  );

  const { isDragOver, isValidFileType, dragHandlers } = useFileDragAndDrop({ onFilesDrop: handleFilesDrop, acceptedFileTypes });

  const deleteClip = useCallback(
    (clipName: string) => {
      setFieldsValues({ [VoiceFormFields.VOICE_CLIP]: values.voiceClip.filter((clip: File) => clip.name !== clipName) });
    },
    [setFieldsValues, values.voiceClip],
  );

  const deletePlaceholderClip = useCallback(
    (clipName: string) => {
      setFieldsValues({
        [VoiceFormFields.PLACEHOLDER_CLIP]: (voiceForm.values.placeholderVoiceClip ?? EMPTY_ARRAY).filter(
          (clip: VoiceClipPlaceholder) => clip.filename !== clipName,
        ),
      });
    },
    [setFieldsValues, voiceForm.values.placeholderVoiceClip],
  );

  return { isDragOver, isValidFileType, dragHandlers, handleFilePicker, deleteClip, deletePlaceholderClip };
}

function useFormActions(
  props: VoiceFormProps,
  voiceForm: VoiceFormInstance,
  refValidClips: React.RefObject<IValidationRef>,
  isStepPristine: boolean,
  hasFormChanged: boolean,
) {
  const { onClone } = props;
  const { displayToast } = useToast();
  const { values } = voiceForm;

  const [stateIsCloneLoading, setStateIsCloneLoading] = useState(false);
  const [stateShowRequiredFormFieldsError, setStateShowRequiredFormFieldsError] = useState(false);
  const [stateShowConsentModal, setStateShowConsentModal] = useState(false);
  const [stateError] = useState("");

  const hasUnsavedChanges = !stateIsCloneLoading && !isStepPristine && hasFormChanged;

  const fileHandling = useFileHandling(voiceForm);

  const prepareVoicePayload = useCallback(
    (): VoiceFormPayload => ({
      name: values[VoiceFormFields.VOICE_NAME],
      description: values[VoiceFormFields.DESCRIPTION],
      language: values[VoiceFormFields.LANGUAGE] as VoiceFormPayload["language"],
      sampleFiles: [
        ...(values[VoiceFormFields.PLACEHOLDER_CLIP] ?? EMPTY_ARRAY).map((clip: VoiceClipPlaceholder) => ({ id: clip.id })),
        ...(values[VoiceFormFields.VOICE_CLIP] ?? EMPTY_ARRAY),
      ],
    }),
    [values],
  );

  const validateBeforeClone = useCallback(() => {
    if (!voiceForm.isValid) {
      setStateShowRequiredFormFieldsError(true);
      scrollToFirstInvalidField(findFirstInvalidFormizField({ fields: voiceForm.fields, fieldOrder: cloneVoiceRequiredFields }));
      return false;
    }
    return true;
  }, [voiceForm.fields, voiceForm.isValid]);

  const submitForm = useCallback(async () => {
    if (!validateBeforeClone()) return;
    setStateShowConsentModal(true);
  }, [validateBeforeClone]);

  const confirmClone = useCallback(async () => {
    setStateIsCloneLoading(true);
    const payload = prepareVoicePayload();
    refValidClips.current!.stopCurrentPlayingClip?.();
    try {
      await onClone?.(payload);
    } finally {
      setStateIsCloneLoading(false);
    }
  }, [onClone, prepareVoicePayload, refValidClips]);

  const handleConsentModalClose = useCallback(() => setStateShowConsentModal(false), []);

  useEffect(() => {
    if (stateError) displayToast({ message: stateError, type: NotificationType.error });
  }, [stateError, displayToast]);

  const routerPromptCallback = useCallback(
    ({ currentLocation, nextLocation }: Parameters<BlockerFunction>[0]) => {
      if (!hasUnsavedChanges) return false;
      return currentLocation.pathname !== nextLocation.pathname;
    },
    [hasUnsavedChanges],
  );

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  return {
    stateIsCloneLoading,
    stateShowRequiredFormFieldsError,
    stateShowConsentModal,
    stateError,
    hasUnsavedChanges,
    submitForm,
    confirmClone,
    handleConsentModalClose,
    routerPromptCallback,
    ...fileHandling,
  };
}

export function useCloneVoiceFormLogic(props: VoiceFormProps, voiceForm: VoiceFormInstance) {
  const refValidClips = useRef<IValidationRef>({ clips: {}, stopCurrentPlayingClip: null });
  const initResult = useFormInitialization(props, voiceForm, refValidClips);
  const { isStepPristine, values, hasFormChanged } = initResult;

  const hasAnyClips = arrLen(values[VoiceFormFields.VOICE_CLIP]) > 0 || arrLen(values[VoiceFormFields.PLACEHOLDER_CLIP]) > 0;
  const isAtClipLimit = arrLen(values[VoiceFormFields.VOICE_CLIP]) + arrLen(values[VoiceFormFields.PLACEHOLDER_CLIP]) >= MAX_VOICE_CLIPS;
  const actionsResult = useFormActions(props, voiceForm, refValidClips, isStepPristine ?? false, hasFormChanged ?? false);

  return { ...initResult, ...actionsResult, refValidClips, hasAnyClips, isAtClipLimit };
}
